'use client';

import { useState, useCallback, useEffect } from 'react';
import { useUpdate } from '@refinedev/core';
import { ArrowUpDown, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SortableList, DragHandle } from '@/components/dnd';

interface ReorderItem {
  id: string | number;
  order: number;
}

interface ReorderModalProps<T extends ReorderItem> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  title?: string;
  description?: string;
  meta?: Record<string, unknown>;
  onSuccess?: () => void;
}

/**
 * Универсальная модалка для изменения порядка сущностей
 *
 * @example
 * ```tsx
 * <ReorderModal
 *   open={reorderOpen}
 *   onOpenChange={setReorderOpen}
 *   resource="subscription-plans"
 *   items={plans}
 *   renderItem={(plan) => <span>{plan.name}</span>}
 *   title="Изменить порядок планов"
 * />
 * ```
 */
export function ReorderModal<T extends ReorderItem>({
  open,
  onOpenChange,
  resource,
  items,
  renderItem,
  title = 'Изменить порядок',
  description = 'Перетащите элементы для изменения порядка отображения',
  meta,
  onSuccess,
}: ReorderModalProps<T>) {
  const [localItems, setLocalItems] = useState<T[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: updateItem } = useUpdate();

  // Синхронизируем локальный стейт с пропсами при открытии
  useEffect(() => {
    if (open) {
      // Сортируем по текущему order
      const sorted = [...items].sort((a, b) => a.order - b.order);
      setLocalItems(sorted);
    }
  }, [open, items]);

  const handleReorder = useCallback((reorderedItems: T[]) => {
    setLocalItems(reorderedItems);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Обновляем только те элементы, чей order изменился
      const updates: Promise<unknown>[] = [];

      localItems.forEach((item, index) => {
        if (item.order !== index) {
          updates.push(
            updateItem({
              resource,
              id: item.id,
              values: { order: index },
              meta,
            })
          );
        }
      });

      await Promise.all(updates);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save order:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = localItems.some((item, index) => item.order !== index);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {localItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Нет элементов для сортировки
            </div>
          ) : (
            <SortableList
              items={localItems}
              onReorder={handleReorder}
              renderItem={({ item, dragHandleProps }) => (
                <div className="flex items-center gap-3 p-3 bg-muted/50 border rounded-lg">
                  <div {...dragHandleProps}>
                    <DragHandle />
                  </div>
                  <div className="flex-1 min-w-0">{renderItem(item)}</div>
                </div>
              )}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Сохранение...
              </>
            ) : (
              'Сохранить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
